import React, { useState, useCallback, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Code } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { AnnouncementTypeEnum, AnnouncementPriorityEnum, AnnouncementVisibilityEnum, CreateAnnouncementData } from "../../modules/shared/model/AnnouncementInterfaces";
import { useAppDispatch, useAppSelector, RootState } from "../../redux/store";
import { GlobalService } from "../../modules/shared/services/global.service";
import CountryDropdown from "../../components/ui/CountryDropdown";
import RegionDropdown from "../../components/ui/RegionDropdown";

interface AnnouncementModalProps {
  announcement: any;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateAnnouncementData, isEdit: boolean) => Promise<void>;
  formData: any;
  setFormData: (data: any) => void;
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  announcement,
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData
}) => {
  const dispatch = useAppDispatch();
  
  const [activeTab, setActiveTab] = useState("content");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [contentType, setContentType] = useState<"text" | "html">("text");

  // Handle country change
  const handleCountryChange = useCallback((country: { id: string; name: string; code: string } | null) => {
    setFormData({
      ...formData,
      country: country || { id: "", name: "", code: "" },
      region: { id: "" } // Reset region when country changes
    });
  }, [formData, setFormData]);

  // Handle region change
  const handleRegionChange = useCallback((regionId: string) => {
    setFormData({
      ...formData,
      region: { id: regionId }
    });
  }, [formData, setFormData]);

  // Initialize form data when editing
  React.useEffect(() => {
    if (announcement && isOpen) {
      console.log("🔍 Modal received form data:", formData);
      // Set content type based on isText field
      setContentType(formData.isText !== false ? "text" : "html");
      // Ensure dates are properly formatted for datetime-local input
      const formattedData = {
        ...formData,
        publishDate: formData.publishDate || "",
        expiryDate: formData.expiryDate || ""
      };
      console.log("🔍 Modal formatted data:", formattedData);
      setFormData(formattedData);
    } else if (isOpen && !announcement) {
      // Reset content type for new announcements
      setContentType("text");
    }
  }, [announcement, isOpen, setFormData]);

  // Type options
  const typeOptions = useMemo(() => [
    { value: AnnouncementTypeEnum.General, label: "General" },
    { value: AnnouncementTypeEnum.Maintenance, label: "Maintenance" },
    { value: AnnouncementTypeEnum.Feature_Update, label: "Feature Update" },
    { value: AnnouncementTypeEnum.Alert, label: "Alert" }
  ], []);

  // Priority options
  const priorityOptions = useMemo(() => [
    { value: AnnouncementPriorityEnum.Low, label: "Low" },
    { value: AnnouncementPriorityEnum.Medium, label: "Medium" },
    { value: AnnouncementPriorityEnum.High, label: "High" }
  ], []);

  // Visibility options
  const visibilityOptions = useMemo(() => [
    { value: AnnouncementVisibilityEnum.All_Users, label: "All Users" },
    { value: AnnouncementVisibilityEnum.Clients, label: "Clients" },
    { value: AnnouncementVisibilityEnum.Professionals, label: "Professionals" }
  ], []);

  const validateForm = useCallback(() => {
    const errors: {[key: string]: string} = {};

    // Required field validations
    if (!formData.title || formData.title.trim() === "") {
      errors.title = "Title is required";
    }

    if (!formData.content || formData.content.trim() === "") {
      errors.content = "Description is required";
    }

    // publishDate, expiryDate, and country are now optional

    // Date validation - only if both dates are provided
    if (formData.publishDate && formData.expiryDate) {
      const publishDate = new Date(formData.publishDate);
      const expiryDate = new Date(formData.expiryDate);
      
      if (expiryDate <= publishDate) {
        errors.expiryDate = "Expiry date must be after publish date";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    // Clear previous validation errors
    setValidationErrors({});

    // Validate form
    if (!validateForm()) {
      GlobalService.showToast("error", "Validation Error", "Please fill in all required fields correctly");
      return;
    }

    // Prepare submission data - only include optional fields if specified
    const submissionData = { ...formData };
    
    // Remove region if not selected
    if (!submissionData.region?.id) {
      delete submissionData.region;
    }
    
    // Remove country if not selected
    if (!submissionData.country?.id) {
      delete submissionData.country;
    }
    
    // Remove dates if not specified
    if (!submissionData.publishDate) {
      delete submissionData.publishDate;
    }
    
    if (!submissionData.expiryDate) {
      delete submissionData.expiryDate;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(submissionData, !!announcement);
    } catch (error: any) {
      // Show API error in toast
      const errorMessage = error?.message || error?.payload || "An error occurred while saving the announcement";
      GlobalService.showToast("error", "Save Failed", errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, announcement, onSubmit, validateForm]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {announcement ? "Edit Announcement" : "Create Announcement"}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6" style={{ textAlign: "left" }}>
            {/* Tabs */}
            <div className="border-b">
              <div className="flex gap-4">
                {["content", "targeting", "schedule"].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 border-b-2 capitalize ${
                      activeTab === tab
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
              {activeTab === "content" && (
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter announcement title"
                      className={`mt-1 w-full px-4 py-2 border rounded-lg ${
                        validationErrors.title ? "border-red-500" : ""
                      }`}
                    />
                    {validationErrors.title && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.title}</p>
                    )}
                  </div>

                  {/* Content */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Description *</label>
                      <div className="flex rounded-md overflow-hidden border">
                        <button
                          type="button"
                          className={`px-3 py-1 text-xs ${contentType === "text" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                          onClick={() => {
                            setContentType("text");
                            setFormData({ ...formData, isText: true });
                          }}
                        >
                          Text
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 text-xs ${contentType === "html" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                          onClick={() => {
                            setContentType("html");
                            setFormData({ ...formData, isText: false });
                          }}
                        >
                          HTML
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      {contentType === "html" && <Code className="h-5 w-5 text-gray-400 mt-2" />}
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        placeholder={contentType === "html" ? "Enter HTML content" : "Enter announcement description"}
                        rows={6}
                        className={`flex-1 px-4 py-2 border rounded-lg ${contentType === "html" ? "font-mono" : ""} ${
                          validationErrors.content ? "border-red-500" : ""
                        }`}
                      />
                    </div>
                    {contentType === "html" && (
                      <p className="text-xs text-gray-500 mt-1">You can use HTML tags to format your content.</p>
                    )}
                    {validationErrors.content && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.content}</p>
                    )}
                  </div>

                  {/* Type & Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Type</label>
                      <select
                        value={formData.type?.id || ""}
                        onChange={(e) => setFormData({ ...formData, type: { id: Number(e.target.value) } })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="">Select Type</option>
                        {typeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Priority</label>
                      <select
                        value={formData.priority?.id || ""}
                        onChange={(e) => setFormData({ ...formData, priority: { id: Number(e.target.value) } })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="">Select Priority</option>
                        {priorityOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "targeting" && (
                <div className="space-y-6">
                  {/* Visibility */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Visibility</label>
                    <select
                      value={formData.visibility?.id || ""}
                      onChange={(e) => setFormData({ ...formData, visibility: { id: Number(e.target.value) } })}
                      className="mt-1 w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="">Select Visibility</option>
                      {visibilityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country (Optional)</label>
                    <CountryDropdown
                      selectedCountry={formData.country?.id ? formData.country : null}
                      onCountrySelect={handleCountryChange}
                      placeholder="Select a country (optional - all locations if not specified)"
                      className={validationErrors.country ? "border-red-500" : ""}
                    />
                    {validationErrors.country && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.country}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Leave empty to target all locations</p>
                  </div>

                  {/* Region */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Region</label>
                    <RegionDropdown
                      countryId={formData.country?.id}
                      value={formData.region?.id}
                      onChange={handleRegionChange}
                      placeholder="Select a region (optional)"
                    />
                  </div>

                  {/* Distribution Channels */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Distribution Channels
                    </label>
                    <div className="space-y-2">
                      {[
                        { key: "isWeb", label: "Web" },
                        { key: "isEmail", label: "Email" },
                        { key: "isMobile", label: "Mobile" }
                      ].map((channel) => (
                        <label key={channel.key} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData[channel.key] || false}
                            onChange={(e) => setFormData({ ...formData, [channel.key]: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          <span>{channel.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "schedule" && (
                <div className="space-y-4">
                  {/* Publish Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Publish Date (Optional)</label>
                    <input
                      type="datetime-local"
                      value={formData.publishDate}
                      onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
                      className={`mt-1 w-full px-4 py-2 border rounded-lg ${
                        validationErrors.publishDate ? "border-red-500" : ""
                      }`}
                    />
                    {validationErrors.publishDate && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.publishDate}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Leave empty to publish immediately</p>
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expiry Date (Optional)</label>
                    <input
                      type="datetime-local"
                      value={formData.expiryDate}
                      onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                      className={`mt-1 w-full px-4 py-2 border rounded-lg ${
                        validationErrors.expiryDate ? "border-red-500" : ""
                      }`}
                    />
                    {validationErrors.expiryDate && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.expiryDate}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Leave empty to never expire</p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : (announcement ? "Update Announcement" : "Create Announcement")}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AnnouncementModal;
